// ModalOrdenPago.jsx
import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Table } from "react-bootstrap";
import moment from "moment";
import { postOrdenPago } from "../helpers/rutaOrdenesPago.js";
import { generarPDFOrdenPago } from "../helpers/pdfOrdenPago";
import { getComandasPendientesTercero } from "../helpers/rutaComandas";
import { generarPDFSaldoCliente } from "../helpers/pdfSaldoCliente";

// 👇 combos de origen de fondos
import { getCajas } from "../helpers/rutaCajas";
import { getCuentas as getCuentasBancarias } from "../helpers/rutaCuentasBancarias"; // debe devolver {cuentas: [...]}

export default function ModalOrdenPago({
  show,
  onHide,
  tercero,
  cliente = null,
  comandasPreseleccionadas = []
}) {
  const [pendientes, setPendientes] = useState([]);
  const [seleccion, setSeleccion] = useState([]);
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [observaciones, setObservaciones] = useState("");
  const [filtrarCliente, setFiltrarCliente] = useState(false);

  // medios de pago (cada item puede tener: medio, importe, caja, cuentaBancaria, nroCheque, fechaCheque, referencia)
  const [medios, setMedios] = useState([]);
  const [rets, setRets] = useState([]);

  // combos y validación de saldos
  const [cajas, setCajas] = useState([]);
  const [cuentas, setCuentas] = useState([]);

  // ---- totales
  const totalAplic = useMemo(
    () => seleccion.reduce((a, c) => a + Number(c.saldo || 0), 0),
    [seleccion]
  );
  const totalPagos = useMemo(
    () => medios.reduce((a, m) => a + Number(m.importe || 0), 0),
    [medios]
  );
  const totalRets = useMemo(
    () => rets.reduce((a, r) => a + Number(r.importe || 0), 0),
    [rets]
  );
  const diff = useMemo(
    () => (totalAplic - totalPagos - totalRets).toFixed(2),
    [totalAplic, totalPagos, totalRets]
  );

  // cargar pendientes + combos
  useEffect(() => {
    if (!show || !(tercero?._id || tercero)) return;

    (async () => {
      // pendientes
      const list = await getComandasPendientesTercero(
        tercero?._id || tercero,
        filtrarCliente ? (cliente?._id || cliente) : undefined
      );
      const ordenadas = (list || []).slice().sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));

      const byId = new Map(ordenadas.map(x => [x._id, x]));
      (comandasPreseleccionadas || []).forEach((c) => {
        if (!byId.has(c._id)) {
          byId.set(c._id, {
            _id: c._id,
            codcom: c.codcom,
            nroviaje: c.nroviaje,
            fecha: c.fecha,
            factura: c.factura || "",
            pactado: Number(c.pactado ?? 0),
            pagado:  Number(c.pagado  ?? 0),
            saldo:   Number(c.saldo   ?? ((c.pactado ?? 0) - (c.pagado ?? 0))),
          });
        }
      });
      const merged = Array.from(byId.values()).sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));
      setPendientes(merged);

      // refresco selección combinada (mantiene lo ya tildado)
      const mapSel = new Map();
      const seed = [
        ...(comandasPreseleccionadas || []).map(c => {
          const found = byId.get(c._id);
          return {
            _id: c._id,
            codcom: (found?.codcom ?? c.codcom),
            nroviaje: (found?.nroviaje ?? c.nroviaje),
            fecha: (found?.fecha ?? c.fecha),
            factura: (found?.factura ?? c.factura),
            pactado: Number(found?.pactado ?? (c.pactado ?? 0)),
            pagado:  Number(found?.pagado  ?? (c.pagado  ?? 0)),
            saldo:   Number((found?.saldo ?? c.saldo ?? ((found?.pactado ?? 0) - (found?.pagado ?? 0))) || 0),
          };
        }),
        ...seleccion,
      ];
      seed.forEach(x => mapSel.set(x._id, x));
      setSeleccion(Array.from(mapSel.values()).filter(x => Number(x.saldo || 0) > 0.01));

      // combos
      try {
        const [cx, bx] = await Promise.all([getCajas(), getCuentasBancarias()]);
        setCajas(cx?.cajas || cx?.data || cx || []);
        setCuentas(bx?.cuentas || bx?.data || bx || []);
      } catch (e) {
        // si falla combos, seguimos con lo disponible
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, tercero?._id, cliente?._id, filtrarCliente]);

  const toggleSeleccion = (c) => {
    setSeleccion(prev => {
      const exists = prev.find(x => x._id === c._id);
      if (exists) return prev.filter(x => x._id !== c._id);
      return [...prev, { ...c, saldo: Number(c.saldo ?? ((c.pactado || 0) - (c.pagado || 0))) }];
    });
  };
  const seleccionarTodas = () => {
    const habilitadas = pendientes
      .filter(p => Number(p.saldo || 0) > 0.01)
      .map(p => ({ ...p, saldo: Number(p.saldo || 0) }));
    setSeleccion(habilitadas);
  };
  const deseleccionarTodas = () => setSeleccion([]);

  // ---- helpers medios / retenciones
  const addMedio = (m) => setMedios(prev => [...prev, m]);
  const removeMedio = (i) => setMedios(prev => prev.filter((_, idx) => idx !== i));
  const addRet   = (r) => setRets(prev => [...prev, r]);
  const removeRet = (i) => setRets(prev => prev.filter((_, idx) => idx !== i));

  // Validación de saldos por medio
  const saldoWarnings = useMemo(() => {
    const warns = [];
    medios.forEach((p, idx) => {
      const imp = Number(p.importe || 0);
      if (p.medio === "efectivo") {
        const cx = cajas.find(c => String(c._id) === String(p.caja));
        const saldo = Number(cx?.saldoActual || 0);
        if (p.caja && imp > saldo + 0.0001) warns.push(`Pago #${idx + 1}: importe supera saldo de caja (${saldo.toLocaleString("es-AR")}).`);
        if (!p.caja) warns.push(`Pago #${idx + 1}: seleccione una Caja para efectivo.`);
      }
      if (p.medio === "transferencia") {
        const ct = cuentas.find(c => String(c._id) === String(p.cuentaBancaria));
        const saldo = Number(ct?.saldoActual || 0);
        if (p.cuentaBancaria && imp > saldo + 0.0001) warns.push(`Pago #${idx + 1}: importe supera saldo de cuenta (${saldo.toLocaleString("es-AR")}).`);
        if (!p.cuentaBancaria) warns.push(`Pago #${idx + 1}: seleccione una Cuenta para transferencia.`);
      }
    });
    return warns;
  }, [medios, cajas, cuentas]);

  const canSubmit =
    Math.abs(Number(diff)) <= 0.01 &&
    totalPagos > 0 &&
    totalAplic > 0 &&
    saldoWarnings.length === 0 &&
    seleccion.length > 0;

  // ---- guardar (OP definitiva)
  const guardar = async () => {
    if (!seleccion.length) return alert("Seleccioná al menos una comanda.");
    if (Math.abs(Number(diff)) > 0.01) {
      return alert("Los totales no cierran (ΣAplicado − ΣPagos − ΣRetenciones = 0).");
    }
    if (saldoWarnings.length) {
      return alert(saldoWarnings.join("\n"));
    }

    const payload = {
      tercero: tercero?._id || tercero,
      cliente: filtrarCliente ? (cliente?._id || cliente) : undefined,
      fecha,
      observaciones,
      aplicaciones: seleccion.map(c => ({
        comanda: c._id,
        aplicado: Number(c.saldo || 0),
      })),
      pagos: medios.map(m => {
        // Normalización de medio para compatibilidad con back actual
        const medio = m.medio === "icheq" ? "cheque" : (m.medio || "transferencia");
        const base = {
          medio,
          importe: Number(m.importe || 0),
          referencia: m.referencia || (m.medio === "icheq" ? "ICHEQ" : ""),
          nroCheque: m.nroCheque || "",
          fechaCheque: m.fechaCheque || null,
          banco: m.banco || "",
          cbu: m.cbu || "",
          observaciones: m.observaciones || "",
        };
        if (medio === "efectivo")   return { ...base, caja: m.caja };
        if (medio === "transferencia") return { ...base, cuentaBancaria: m.cuentaBancaria };
        return base; // cheque/icheq (sin impacto de saldo en back actual)
      }),
      retenciones: rets.map(r => ({
        tipo: (r.tipo || "IIBB").toUpperCase(),
        base: Number(r.base || 0),
        porcentaje: Number(r.porcentaje || 0),
        importe: Number(r.importe || 0),
        comprobante: r.comprobante || "",
      })),
    };

    const res = await postOrdenPago(payload);
    if (res?.ok) {
      alert("Orden de pago guardada correctamente.");
      try { generarPDFOrdenPago(res.orden); } catch (e) { console.error("PDF OP:", e); }
      onHide(true);
    } else {
      console.error("OP error:", res);
      alert(res?.msg || "Error al guardar la orden de pago.");
    }
  };

  // ---- Exportar PDFs (sin guardar)
  const nombreTercero = (tercero?.transporte || tercero?.razonsocial || tercero?.nombre || "").toString();
  const nombreCliente = (cliente?.razonsocial || cliente?.nombre || "")?.toString() || "";

  const buildItems = (lista) => {
    const pick = (obj, ...keys) => keys.find(k => obj?.[k] != null);
    const toNum  = v => Number(v || 0);
    const toDate = v => (v ? new Date(v) : null);
    const toStr  = v => (v == null ? "" : String(v));

    return lista.map(c => {
      const numero   = toStr(c[pick(c,"codcom","numero","nrodecomanda","num")] || "");
      const nroviaje = toStr(c[pick(c,"nroviaje","nroViaje","nro_viaje","nro","nrovia","nro_via")] || "");
      const fecha    = toDate(c[pick(c,"fecha","fec","fech")] || null);
      const pactado  = toNum(c[pick(c,"pactado","total","Total")] || 0);
      const pagado   = toNum(c[pick(c,"pagado","aplicado")] || 0);
      const saldo    = toNum(c[pick(c,"saldo","Saldo")] || 0);
      return { numero, nroviaje, fecha, total: pactado, aplicado: pagado, saldo };
    });
  };

  const exportarTodoPDF = () => {
    if (!(pendientes?.length)) return alert("No hay comandas pendientes para exportar.");
    const items = buildItems(pendientes);
    const totalSaldo = items.reduce((s,i) => s + Number(i.saldo||0), 0);

    generarPDFSaldoCliente({
      clienteNombre: `${nombreTercero}${filtrarCliente && nombreCliente ? " — Cliente: " + nombreCliente : ""}`,
      fechaCorte: fecha ? new Date(fecha) : new Date(),
      data: { totalSaldoComandas: totalSaldo, comandasConSaldo: items, ordenesCobroAbiertas: [] },
      titulo: "SALDOS PENDIENTES A TERCEROS",
    });
  };

  const exportarSeleccionadasPDF = () => {
    if (!(seleccion?.length)) return alert("No hay comandas seleccionadas para exportar.");
    const items = buildItems(seleccion);
    const totalSaldo = items.reduce((s,i) => s + Number(i.saldo||0), 0);

    generarPDFSaldoCliente({
      clienteNombre: `${nombreTercero}${filtrarCliente && nombreCliente ? " — Cliente: " + nombreCliente : ""}`,
      fechaCorte: fecha ? new Date(fecha) : new Date(),
      data: { totalSaldoComandas: totalSaldo, comandasConSaldo: items, ordenesCobroAbiertas: [] },
      titulo: "SALDOS PENDIENTES A TERCEROS",
    });
  };

  const $ = (n) => Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Modal show={show} onHide={() => onHide(false)} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Nueva Orden de Pago</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-2">
          <Col md={4}>
            <Form.Label>Tercero</Form.Label>
            <Form.Control readOnly value={nombreTercero} />
          </Col>
          <Col md={4}>
            <Form.Label>Cliente (filtro opcional)</Form.Label>
            <Form.Control readOnly value={nombreCliente} placeholder="(sin filtro)" />
            <div className="mt-1">
              <Form.Check
                type="switch"
                id="filtrar-por-cliente"
                label="Filtrar por este cliente"
                checked={filtrarCliente}
                onChange={(e) => setFiltrarCliente(e.target.checked)}
              />
            </div>
          </Col>
          <Col md={4}>
            <Form.Label>Fecha</Form.Label>
            <Form.Control type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
          </Col>
        </Row>

        {/* LISTA DE PENDIENTES */}
        <Row className="align-items-center mb-2">
          <Col><b>Comandas pendientes del tercero</b></Col>
          <Col className="text-end">
            <Button size="sm" variant="outline-secondary" onClick={seleccionarTodas}>Tildar todas</Button>{" "}
            <Button size="sm" variant="outline-secondary" onClick={deseleccionarTodas}>Destildar todas</Button>{" "}
            <Button size="sm" variant="success" className="ms-1" onClick={exportarTodoPDF} style={{ minWidth: 150 }}>
              Exportar TODO PDF
            </Button>
            <Button
              size="sm"
              variant="outline-success"
              className="ms-1"
              onClick={exportarSeleccionadasPDF}
              disabled={!seleccion.length}
              style={{ minWidth: 190 }}
            >
              Exportar SELECCIONADAS PDF
            </Button>
            {Boolean(seleccion.length) && (
              <span className="ms-2">
                Seleccionadas: <b>{seleccion.length}</b> — Σ: <b>${$(totalAplic)}</b>
              </span>
            )}
          </Col>
        </Row>

        <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #e0e0e0", position: "relative" }}>
          <Table size="sm" bordered hover className="m-0">
            <thead>
              <tr style={{ position: "sticky", top: 0, background: "#f8f9fa", zIndex: 2 }}>
                <th style={{ width: 40, textAlign: "center" }}></th>
                <th>Comanda</th>
                <th>Viaje</th>
                <th>Fecha</th>
                <th>Factura</th>
                <th style={{ textAlign: "right" }}>Pactado</th>
                <th style={{ textAlign: "right" }}>Pagado</th>
                <th style={{ textAlign: "right" }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {pendientes.map((c) => {
                const checked = !!seleccion.find((x) => x._id === c._id);
                const disabled = Number(c.saldo || 0) <= 0.01;
                const inputId = `sel-op-${c._id}`;
                return (
                  <tr key={c._id} style={disabled ? { opacity: 0.5 } : undefined}>
                    <td style={{ width: 40, textAlign: "center" }}>
                      <input
                        id={inputId}
                        type="checkbox"
                        className="form-check-input m-0 p-0"
                        style={{ marginLeft: 0, position: "static" }}
                        disabled={disabled}
                        checked={checked}
                        onChange={() => toggleSeleccion(c)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td><label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                      #{String(c.codcom).padStart(6, "0")}
                    </label></td>
                    <td><label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                      {c.nroviaje}
                    </label></td>
                    <td><label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                      {c.fecha ? moment(c.fecha).format("DD/MM/YYYY") : ""}
                    </label></td>
                    <td><label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                      {c.factura || ""}
                    </label></td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={inputId} className="w-100 m-0">{`$${$(c.pactado ?? 0)}`}</label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={inputId} className="w-100 m-0">{`$${$(c.pagado ?? 0)}`}</label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={inputId} className="w-100 m-0">{`$${$(c.saldo ?? 0)}`}</label>
                    </td>
                  </tr>
                );
              })}
              {!pendientes.length && (
                <tr>
                  <td colSpan={8} className="text-center text-muted">
                    Sin pendientes para este tercero/cliente
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <Row className="mt-3">
          <Col md={6}>
            <h6>Pagos</h6>
            <Button size="sm" onClick={() => addMedio({ medio: "transferencia", importe: 0, cuentaBancaria: "" })}>+ Transferencia</Button>{" "}
            <Button size="sm" onClick={() => addMedio({ medio: "cheque", importe: 0, nroCheque: "", fechaCheque: "" })}>+ Cheque</Button>{" "}
            <Button size="sm" onClick={() => addMedio({ medio: "icheq", importe: 0, nroCheque: "", fechaCheque: "", referencia: "ICHEQ" })}>+ iCheq</Button>{" "}
            <Button size="sm" onClick={() => addMedio({ medio: "efectivo", importe: 0, caja: "" })}>+ Efectivo</Button>
            <Table size="sm" className="mt-2">
              <thead><tr><th>Medio</th><th>Origen / Datos</th><th>Importe</th><th></th></tr></thead>
              <tbody>
                {medios.map((m, i) => {
                  const medio = m.medio;
                  const selCaja = cajas.find(c => String(c._id) === String(m.caja));
                  const selCta  = cuentas.find(c => String(c._id) === String(m.cuentaBancaria));
                  return (
                    <tr key={i}>
                      <td style={{ width: 120 }}>
                        <Form.Select
                          size="sm"
                          value={medio}
                          onChange={(e) => setMedios(prev => prev.map((x, idx) =>
                            idx === i ? { ...x, medio: e.target.value, caja: "", cuentaBancaria: "" } : x
                          ))}
                        >
                          <option value="transferencia">Transferencia</option>
                          <option value="cheque">Cheque</option>
                          <option value="icheq">iCheq</option>
                          <option value="efectivo">Efectivo</option>
                        </Form.Select>
                      </td>

                      <td>
                        {medio === "efectivo" && (
                          <div className="d-flex gap-2">
                            <Form.Select size="sm" value={m.caja || ""} style={{ minWidth: 260 }}
                              onChange={(e) => setMedios(prev => prev.map((x, idx) => idx === i ? { ...x, caja: e.target.value } : x))}
                            >
                              <option value="">-- Caja --</option>
                              {cajas.map(c => (
                                <option key={c._id} value={c._id}>
                                  {c.descripcion} — Saldo: {(Number(c.saldoActual||0)).toLocaleString("es-AR")}
                                </option>
                              ))}
                            </Form.Select>
                            {selCaja && <span className="text-muted">Saldo: {(Number(selCaja?.saldoActual||0)).toLocaleString("es-AR")}</span>}
                          </div>
                        )}

                        {medio === "transferencia" && (
                          <div className="d-flex gap-2">
                            <Form.Select size="sm" value={m.cuentaBancaria || ""} style={{ minWidth: 260 }}
                              onChange={(e) => setMedios(prev => prev.map((x, idx) => idx === i ? { ...x, cuentaBancaria: e.target.value } : x))}
                            >
                              <option value="">-- Cuenta --</option>
                              {cuentas.map(c => (
                                <option key={c._id} value={c._id}>
                                  {(c.alias || c.numero || "Cuenta")} — Saldo: {(Number(c.saldoActual||0)).toLocaleString("es-AR")}
                                </option>
                              ))}
                            </Form.Select>
                            {selCta && <span className="text-muted">Saldo: {(Number(selCta?.saldoActual||0)).toLocaleString("es-AR")}</span>}
                          </div>
                        )}

                        {(medio === "cheque" || medio === "icheq") && (
                          <div className="d-flex gap-2">
                            <Form.Control size="sm" placeholder={medio === "icheq" ? "Ref iCheq" : "N° Cheque / Ref"}
                              value={m.referencia || m.nroCheque || ""}
                              onChange={(e) => setMedios(prev => prev.map((x, idx) => idx === i
                                ? { ...x, referencia: e.target.value, nroCheque: e.target.value }
                                : x
                              ))}
                              style={{ minWidth: 200 }}
                            />
                            <Form.Control size="sm" type="date"
                              value={m.fechaCheque || ""}
                              onChange={(e) => setMedios(prev => prev.map((x, idx) => idx === i ? { ...x, fechaCheque: e.target.value } : x))}
                              style={{ maxWidth: 160 }}
                            />
                          </div>
                        )}
                      </td>

                      <td style={{ textAlign: "right", width: 160 }}>
                        <Form.Control
                          size="sm"
                          type="number"
                          step="0.01"
                          value={m.importe}
                          onChange={(e) =>
                            setMedios(prev => prev.map((x, idx) => idx === i ? { ...x, importe: Number(e.target.value) } : x))
                          }
                        />
                      </td>
                      <td style={{ width: 50 }}>
                        <Button variant="outline-danger" size="sm" onClick={() => removeMedio(i)}>x</Button>
                      </td>
                    </tr>
                  );
                })}
                {!medios.length && <tr><td colSpan={4} className="text-muted">Agregá al menos un pago</td></tr>}
              </tbody>
            </Table>

            {/* Warnings de saldo */}
            {saldoWarnings.length > 0 && (
              <ul className="mt-2" style={{ color:"crimson" }}>
                {saldoWarnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </Col>

          <Col md={6}>
            <h6>Retenciones</h6>
            <Button size="sm" onClick={() => addRet({ tipo: "IIBB", base: 0, porcentaje: 0, importe: 0 })}>+ IIBB</Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "GAN",  base: 0, porcentaje: 0, importe: 0 })}>+ Ganancias</Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "IVA",  base: 0, porcentaje: 0, importe: 0 })}>+ IVA</Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "OTRA", base: 0, porcentaje: 0, importe: 0 })}>+ Otra</Button>
            <Table size="sm" className="mt-2">
              <thead><tr><th>Tipo</th><th>Base</th><th>%</th><th>Importe</th><th>Comp.</th><th></th></tr></thead>
              <tbody>
                {rets.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <Form.Select
                        size="sm"
                        value={r.tipo}
                        onChange={(e) => setRets(prev => prev.map((x, idx) => idx === i ? { ...x, tipo: e.target.value } : x))}
                      >
                        <option value="IIBB">IIBB</option>
                        <option value="GAN">Ganancias</option>
                        <option value="IVA">IVA</option>
                        <option value="OTRA">OTRA</option>
                      </Form.Select>
                    </td>
                    <td style={{ width: 110 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={r.base || 0}
                        onChange={(e) => setRets(prev => prev.map((x, idx) => idx === i ? { ...x, base: Number(e.target.value) } : x))}
                      />
                    </td>
                    <td style={{ width: 90 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={r.porcentaje || 0}
                        onChange={(e) => setRets(prev => prev.map((x, idx) => idx === i ? { ...x, porcentaje: Number(e.target.value) } : x))}
                      />
                    </td>
                    <td style={{ width: 120 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={r.importe || 0}
                        onChange={(e) => setRets(prev => prev.map((x, idx) => idx === i ? { ...x, importe: Number(e.target.value) } : x))}
                      />
                    </td>
                    <td style={{ width: 140 }}>
                      <Form.Control
                        size="sm"
                        placeholder="Comprobante"
                        value={r.comprobante || ""}
                        onChange={(e) => setRets(prev => prev.map((x, idx) => idx === i ? { ...x, comprobante: e.target.value } : x))}
                      />
                    </td>
                    <td><Button variant="outline-danger" size="sm" onClick={() => removeRet(i)}>x</Button></td>
                  </tr>
                ))}
                {!rets.length && <tr><td colSpan={6} className="text-muted">Agregá retenciones si corresponde</td></tr>}
              </tbody>
            </Table>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col>
            <Form.Label>Observaciones</Form.Label>
            <Form.Control as="textarea" rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={4}><b>Total aplicado (Σ selección):</b> ${$(totalAplic)}</Col>
          <Col md={4}><b>Σ Pagos:</b> ${$(totalPagos)}</Col>
          <Col md={4}><b>Σ Retenciones:</b> ${$(totalRets)}</Col>
        </Row>
        <Row>
          <Col md={12}>
            <b>Diferencia (Aplicado − Pagos − Retenciones):</b>{" "}
            <span style={{ color: Math.abs(Number(diff)) < 0.01 ? "green" : "red" }}>
              {diff}
            </span>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)}>Cancelar</Button>
        <Button
          variant="primary"
          onClick={guardar}
          disabled={!canSubmit}
        >
          Guardar y generar PDF
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
